import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AppState } from '../../../redux/modules/store';
import { getPcmCdNmByPcmCdDivisionFromMasterInfo } from '../../../redux/reducers/MasterInfoReducer/utils/MasterInfoStateUtils';

import { formatDate } from '../../../Common';
import Table from '../../atoms/Table';
import UpdateDate from '../../atoms/UpdateDate';
import { setStringToBrackets } from '../../../utils/StringUtil';
import { JapaneseDateStringType, getJapaneseDateTimeAsString } from '../../../utils/MomentUtil';

import PreviewContents from '../../atoms/PreviewContents';
import PreviewMiddleCategoryHeader from '../../atoms/PreviewMiddleCategoryHeader';
import PreviewHeader from '../../atoms/PreviewHeader';
import BswReceptionContentsTable from '../../molecules/BswReceptionContentsTable';
import GinieReceptionContentsTable from '../../molecules/GinieReceptionContentsTable';
import { isCrewModule } from '../../../utils/Env';

// 改行コード
const lineBreak = '\n';

export interface CustomerInfoPreviewProps {
  isEditable: boolean;
  openPermissionModal?: (type: string) => void;
}

const CustomerInfoPreview: React.FC<CustomerInfoPreviewProps> = ({ isEditable, openPermissionModal }) => {
  // custom Hooks
  const masterInfo = useSelector((state: AppState) => state.masterInfo);
  const customerInfoAll = useSelector((state: AppState) => state.customerInfoAll);
  const history = useHistory();

  const UpdateDateInfo = (msnUpdDate: string) => {
    return (
      <p className='u-text-dark u-ml8'>
        <UpdateDate
          size={'small'}
          updateDate={formatDate(msnUpdDate, 'YYYYMMDD', 'YYYY年MM月DD日')}
          data-testid='msnUpdDate'
          data-testid-e2e='customerInfo-msnUpdDate'
        />
      </p>
    );
  };

  // 日中連絡先
  const contactMsn1 = customerInfoAll.contactMsn1 || '';
  const msnUpdDate = customerInfoAll.msnUpdDate || '';
  const displayContactMsn1 = contactMsn1 ? contactMsn1 : msnUpdDate ? '-' : '';

  // 割れ家族有無
  const familyStructureType = getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0006', customerInfoAll.familyStructureType || '');

  // 家族構成
  const getDisplayFamilyStructureList = (): string => {
    const familyStructureList: string[] = [];

    for (const item of customerInfoAll.familyStructureInfoList) {
      const familyStructureInfoItem = getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0151', item.familyStructureInfoItem);
      if (familyStructureInfoItem && item.setValue && item.setValue > 0) {
        familyStructureList.push(`${familyStructureInfoItem}：${item.setValue}人`);
      }
    }
    return familyStructureList.join(lineBreak);
  };

  // 年齢/人数
  const familyStructureAgeToDisplay =
    customerInfoAll.familyStructureAge !== null && customerInfoAll.familyStructureAgeCount !== null
      ? `${customerInfoAll.familyStructureAge}歳以下${customerInfoAll.familyStructureAgeCount}人`
      : '';

  // メール送信
  const sendMailType = getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0043', customerInfoAll.sendMailType);

  // メール送信許諾
  const mailPermissionType = getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0175', customerInfoAll.mailPermissionType || '');

  // 送信先のSB/YM電話番号
  const sendMailMsnType = getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0044', customerInfoAll.sendMailMsnType || '');

  // 情報保持許諾
  const permissionType = getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0174', customerInfoAll.permissionType || '');

  // 一斉送信メール
  const sendRegulationType = getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0045', customerInfoAll.sendRegulationType || '');

  // 本人確認書類
  const docType = getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0008', customerInfoAll.docType || '');

  // 在留期限年月
  const stayDeadline = (): string => {
    const validStayDeadline = getJapaneseDateTimeAsString(customerInfoAll.stayDeadline, JapaneseDateStringType.YEAR_MONTH);
    if (validStayDeadline === '') {
      return '';
    }
    return `（在留期限：${validStayDeadline}）`;
  };

  // その他
  const otherInput = customerInfoAll.otherInput ? setStringToBrackets(customerInfoAll.otherInput) : ''; // 見込顧客本人確認書類その他入力テキスト

  // 本人確認書類
  const docTypeWithOtherInput = (): string => {
    // 在留カード＋外国パスポートの場合
    if (customerInfoAll.docType === '07') {
      return `${docType}${stayDeadline()}`;
    }

    // その他の場合
    if (customerInfoAll.docType === '99') {
      return `${docType}${otherInput}`;
    }
    return docType;
  };

  // 追加書類
  const otherDoc = (): string => {
    const otherDocList: string[] = [];
    if (customerInfoAll.otherDocTypeConsent === '1') {
      otherDocList.push('同意書');
    }
    if (customerInfoAll.otherDocTypeCommission === '1') {
      otherDocList.push('委任状');
    }
    if (customerInfoAll.otherDocTypeAddress === '1') {
      otherDocList.push('住所確認書類');
    }
    if (customerInfoAll.otherDocTypeFamily === '1') {
      otherDocList.push('家族確認書類');
    }
    return otherDocList.join(lineBreak);
  };
  // 光利用開始日
  const sbbStartDate = customerInfoAll.sbbStartDate
    ? getJapaneseDateTimeAsString(customerInfoAll.sbbStartDate, JapaneseDateStringType.YEAR_MONTH_DATE)
    : '';

  // メール送信回数
  const getDisplayEmailSendCount = () => {
    const currentMonth = `当月：${customerInfoAll.mailSendCountMonthTotal}回（個別：${customerInfoAll.mailSendCountMonthUniCast}回／一斉：${customerInfoAll.mailSendCountMonthMultiCast}回）`;
    const total = `累計：${customerInfoAll.mailSendCountAllTotal}回（個別：${customerInfoAll.mailSendCountAllUniCast}回／一斉：${customerInfoAll.mailSendCountAllMultiCast}回）`;
    return `${currentMonth}\n${total}`;
  };

  return (
    <>
      <div className='u-bg-white preview-header__sticky'>
        <PreviewHeader
          title='お客さま情報'
          uniqueId='customer-preview-header'
          editInfo={
            isEditable
              ? {
                  uniqueId: `${customerInfoAll.customerId}`,
                  label: '編集',
                  onClickEditHandler: () => openPermissionModal && openPermissionModal('onEditCustomer'),
                }
              : undefined
          }
          detailInfo={
            isEditable
              ? {
                  label: '詳細',
                  onClick: () => {
                    history.push(`/customer/detail/${customerInfoAll.customerId}`);
                  },
                }
              : undefined
          }
        />
      </div>
      <Table className='preview-table'>
        {/* middle Header */}
        <PreviewMiddleCategoryHeader title='お客さま情報（基本）' uniqueId='customer-base-header' />
        <Table.TBody>
          <PreviewContents title='氏名' paragraphValue={customerInfoAll.customerFullName || ''} uniqueId='customer-full-name' />
          <PreviewContents
            title='氏名フリガナ'
            paragraphValue={customerInfoAll.customerKanaFullName || ''}
            uniqueId='customer-kana-full-name'
          />
          <PreviewContents title='お客さま番号' paragraphValue={customerInfoAll.customerNumber} uniqueId='customer-number' />
          <PreviewContents title='日中連絡先' uniqueId='customer-msn1-with-msn-update'>
            <div className='contactMsn'>
              <p>{displayContactMsn1}</p>
              {customerInfoAll.msnUpdDate ? UpdateDateInfo(customerInfoAll.msnUpdDate) : ''}
            </div>
          </PreviewContents>
          <PreviewContents title='緊急連絡先' paragraphValue={customerInfoAll.contactMsn2 || ''} uniqueId='customer-contact-msn2' />
        </Table.TBody>
      </Table>
      <Table className='preview-table u-mt32'>
        {/* middle Header */}
        <PreviewMiddleCategoryHeader title='お客さま情報（詳細）' uniqueId='customer-detail-header' />
        <Table.TBody>
          {customerInfoAll.customerNameType === '2' && (
            <>
              <PreviewContents
                title='代表電話番号'
                paragraphValue={customerInfoAll.corpPhoneNumber ? (isCrewModule() ? customerInfoAll.corpPhoneNumber : '入力あり') : ''}
                uniqueId='customer-corp-phone-number'
              />
              <PreviewContents
                title='法人担当者名'
                paragraphValue={customerInfoAll.corpContactName ? (isCrewModule() ? customerInfoAll.corpContactName : '入力あり') : ''}
                uniqueId='customer-corp-contact-name'
              />
              <PreviewContents
                title='法人担当者メールアドレス'
                paragraphValue={
                  customerInfoAll.corpContactMailAddress ? (isCrewModule() ? customerInfoAll.corpContactMailAddress : '入力あり') : ''
                }
                uniqueId='customer-corp-contact-mail-address'
              />
              <PreviewContents
                title='部署名'
                paragraphValue={
                  customerInfoAll.corpDepartmentName ? (isCrewModule() ? customerInfoAll.corpDepartmentName : '入力あり') : ''
                }
                uniqueId='customer-corp-department-name'
              />
              <PreviewContents
                title='役職'
                paragraphValue={customerInfoAll.corpPostName ? (isCrewModule() ? customerInfoAll.corpPostName : '入力あり') : ''}
                uniqueId='customer-corp-post-name'
              />
            </>
          )}
          <PreviewContents title='割れ家族有無' paragraphValue={familyStructureType} uniqueId='customer-family-structure-type' />
          <PreviewContents title='家族構成' paragraphValue={getDisplayFamilyStructureList()} uniqueId='customer-family-structure-list' />
          <PreviewContents title='年齢/人数' paragraphValue={familyStructureAgeToDisplay} uniqueId='customer-family-structure-age' />
          <PreviewContents title='メール送信' paragraphValue={sendMailType} uniqueId='customer-send-mail-type' />
          <PreviewContents title='メール送信許諾' paragraphValue={mailPermissionType} uniqueId='customer-mail-permission-type' />
          <PreviewContents title='送信先のSB/YM電話番号' paragraphValue={sendMailMsnType} uniqueId='customer-send-mail-msn-type' />
          <PreviewContents
            title='送信先のメールアドレス'
            paragraphValue={customerInfoAll.sendMailAddress || ''}
            uniqueId='customer-send-mail-address'
          />
          <PreviewContents title='情報保持許諾' paragraphValue={permissionType} uniqueId='customer-permission-type' />
          <PreviewContents title='一斉送信メール' paragraphValue={sendRegulationType} uniqueId='customer-send-regulation-type' />
          <PreviewContents title='本人確認書類' paragraphValue={docTypeWithOtherInput()} uniqueId='customer-doc-type-with-other-input' />
          <PreviewContents title='追加書類' paragraphValue={otherDoc()} uniqueId='customer-other-doc' />
          <PreviewContents title='メール送信回数' paragraphValue={getDisplayEmailSendCount()} uniqueId='customer-mail-send-count' />
        </Table.TBody>
      </Table>
      <Table className='preview-table u-mt32'>
        {/* middle Header */}
        <PreviewMiddleCategoryHeader title='外部連携情報' uniqueId='customer-external-linkage-information-header' />
        <Table.TBody>
          <PreviewContents title='GINIE登録結果情報' uniqueId='customer-ginie-reception-contents'>
            <div className='scrollable-area'>
              <GinieReceptionContentsTable data={customerInfoAll.ginieReceptionContents} />
            </div>
          </PreviewContents>
          <PreviewContents title='BB-GINIE登録結果情報' uniqueId='customer-bsw-reception-contents'>
            <div className='scrollable-area'>
              <BswReceptionContentsTable data={customerInfoAll.bswReceptionContents} />
            </div>
          </PreviewContents>
          <PreviewContents title='光利用開始日' paragraphValue={sbbStartDate} uniqueId='customer-sbb-start-date' />
        </Table.TBody>
      </Table>
    </>
  );
};

export default CustomerInfoPreview;
